import React, { Fragment, useEffect, useMemo } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import {
	QuickReloadSubscriber,
	ReloadType,
} from '@confluence/quick-reload/entry-points/subscription.ts';
import { ssrContentStateManager } from '@confluence/ssr-utilities/entry-points/SSRContentStateManager.ts';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';

import { LazyEditorContentStateComponent } from '../lazyLoadables.ts';

import { ContentStateStateContainer, defaultValues } from './ContentStateStateContainer.tsx';
import {
	type EditorContentStateQuery$data,
	type EditorContentStateQuery$variables,
} from './__generated__/EditorContentStateQuery.graphql.ts';
import type { ContentStateState, EditorContentStateProps } from './types.ts';

function computeContentStateValuesFromData(
	data: NonNullable<EditorContentStateQuery$data>,
	defaultStatesArray: ContentStateState['defaultStatesArray'],
	customStatesArray: ContentStateState['customStatesArray'],
	isDraft: boolean,
	contentId: string,
): ContentStateState {
	const newContentStateValues = Object.assign({}, defaultValues);
	/**  Computes content state values from query response data. Used for both SSR bootstrap (useEffect never runs on
   server, so we compute state from preloaded data for initial render) and client useEffect (setInitialValues).
   Setting shouldRunContentStateEffect = false prevents the effect from running again on parent re-renders
   (EditorContentState lives in breadcrumbs/title area), which would otherwise reset the values.
	*/
	newContentStateValues.shouldRunContentStateEffect = false;
	newContentStateValues.contentState = data?.singleContent?.contentState || null;
	newContentStateValues.contentStateLastUpdated =
		data?.singleContent?.contentStateLastUpdated?.value || null;
	newContentStateValues.defaultStatesArray = defaultStatesArray;
	newContentStateValues.customStatesArray = customStatesArray;
	const isSuggestedContentStatesDisabled = !data?.availableContentStates?.spaceContentStates;
	const isCustomContentStatesDisabled = !data?.availableContentStates?.customContentStates;
	const isContentStatesDisabled = isSuggestedContentStatesDisabled && isCustomContentStatesDisabled;
	if (isContentStatesDisabled) {
		newContentStateValues.isContentStateEnabled = false;
	} else if (isSuggestedContentStatesDisabled) {
		newContentStateValues.isSuggestedEnabled = false;
	} else if (isCustomContentStatesDisabled) {
		newContentStateValues.isCustomEnabled = false;
	}
	newContentStateValues.lastFetchedIsDraft = isDraft;
	newContentStateValues.lastFetchedContentId = contentId;
	return newContentStateValues;
}

const EditorContentStateDataGetter = ({
	contentId,
	contentType,
	contentStateState,
	isPublishingContent,
	userOffline,
	onContentStateMenuToggle,
	floatingToolbarButtonStyle,
	renderPlusButtonEmptyState,
	isDetailsPanel,
	onClick,
	isViewMode,
	canEdit,
}: EditorContentStateProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isCurrentPageLive = useIsCurrentPageLive();
	const isDraft = !isViewMode && !isCurrentPageLive;
	const { approvalRequest } = useApprovals();
	const hasApprovalStatus = !!approvalRequest?.application?.workflowStatusId;

	const { data, refetch } = useQueryInterop<
		EditorContentStateQuery$data,
		EditorContentStateQuery$variables
	>(
		graphql`
			query EditorContentStateQuery(
				$contentId: ID!
				$isDraft: Boolean!
				$isApprovalsFgOn: Boolean!
				$format: GraphQLDateFormat!
				$shouldSkipContentStateLastUpdated: Boolean!
			) {
				singleContent(id: $contentId) {
					id
					contentState(isDraft: $isDraft) {
						id
						name
						color
						restrictionLevel
						isCallerPermitted
						lastModifierAaid @include(if: $isApprovalsFgOn)
					}
					# currently only view page and live pages require this field to be loaded for the content header
					# if the Approvals experiment is enabled, we include the content state last updated field
					contentStateLastUpdated(format: $format, isDraft: $isDraft)
						@skip(if: $shouldSkipContentStateLastUpdated) {
						value
					}
				}
				availableContentStates(contentId: $contentId) {
					customContentStates {
						name
						color
						id
					}
					spaceContentStates {
						name
						color
						id
						restrictionLevel
					}
				}
			}
		`,
		{
			fetchPolicy: Boolean(window.__SSR_RENDERED__) ? 'cache-first' : 'network-only',
			variables: {
				contentId,
				isDraft,
				shouldSkipContentStateLastUpdated: expValEquals(
					'confluence_approvals_eap',
					'isEnabled',
					true,
				)
					? false
					: !isCurrentPageLive,
				isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
				format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
					? 'MILLIS'
					: 'USER_FRIENDLY',
			} as EditorContentStateQuery$variables,
			skip: expValEquals('confluence_approvals_eap', 'isEnabled', true)
				? false
				: !contentStateState.state.shouldRunContentStateEffect,
		},
	);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && data) {
		ssrContentStateManager.setContentStateData(data);
	}

	// Memoize filtered arrays to avoid expensive operations during render
	const defaultStatesArray = useMemo(
		() => data?.availableContentStates?.spaceContentStates?.filter((i) => !!i) || [],
		[data?.availableContentStates?.spaceContentStates],
	);
	const customStatesArray = useMemo(
		() => data?.availableContentStates?.customContentStates?.filter((i) => !!i) || [],
		[data?.availableContentStates?.customContentStates],
	);

	/**  During SSR, useEffect never runs. When we have preloaded data, compute bootstrap state
	so shouldRenderUI and ContentStateButton receive correct contentState. */
	let ssrContentState: ContentStateState | null = null;
	if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR &&
		data &&
		data.singleContent?.id === contentId &&
		contentStateState.state.lastFetchedIsDraft === null &&
		(expValEquals('confluence_approvals_eap', 'isEnabled', true) ||
			contentStateState.state.shouldRunContentStateEffect)
	) {
		ssrContentState = computeContentStateValuesFromData(
			data,
			defaultStatesArray,
			customStatesArray,
			isDraft,
			contentId,
		);
	}

	const effectiveState = ssrContentState ?? contentStateState.state;

	const isTransitioning =
		// Content State has been fetched before
		contentStateState.state.lastFetchedIsDraft !== null &&
		// for a mode that was different from the current mode
		contentStateState.state.lastFetchedIsDraft !== isDraft &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true);

	useEffect(() => {
		if (isTransitioning) {
			void contentStateState.resetToDefaults();
		}
	}, [isTransitioning, contentStateState]);

	useEffect(() => {
		// When experiment is off, this is always false
		if (isTransitioning) {
			return;
		}
		// When Approvals experiment is on, we only render EditorContentState in the Byline (Breadcrumbs), so
		// we do not get same-page remounts. This means we can safely assign new values any time new data is available.
		// Also verify the data belongs to the current contentId — after a navigation, the query may
		// briefly return cached/stale data from the previous contentId before the new network response arrives.
		if (
			data &&
			data.singleContent?.id === contentId &&
			(expValEquals('confluence_approvals_eap', 'isEnabled', true) ||
				contentStateState.state.shouldRunContentStateEffect)
		) {
			void contentStateState.setInitialValues(
				computeContentStateValuesFromData(
					data,
					defaultStatesArray,
					customStatesArray,
					isDraft,
					contentId,
				),
			);
		}
	}, [
		data,
		contentStateState,
		defaultStatesArray,
		customStatesArray,
		isDraft,
		isTransitioning,
		contentId,
	]);

	const shouldRenderUI =
		!isTransitioning &&
		// Don't render stale content state from a different page during cross-page navigation.
		!(
			effectiveState.lastFetchedContentId !== null &&
			effectiveState.lastFetchedContentId !== contentId &&
			fg('confluence_frontend_transition_preloading_system')
		) &&
		// Don't render the content state UI until data has been fetched for the current context.
		// Use effectiveState so SSR bootstrap (preloaded data) works before setState flushes.
		!(effectiveState.shouldRunContentStateEffect && effectiveState.lastFetchedIsDraft === null) &&
		// When the space setting contentStatesAllowed is false (isContentStateEnabled = false),
		// hide the status button entirely unless the page already has a content state or approval status.
		// Pages with an existing status still show the button so users can see/manage the current state.
		!(
			!effectiveState.isContentStateEnabled &&
			!effectiveState.contentState &&
			!hasApprovalStatus &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true)
		) &&
		// In view mode, don't render the empty Status button for users without edit permission.
		!(
			isViewMode &&
			!canEdit &&
			!(effectiveState.contentState || hasApprovalStatus) &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true)
		) &&
		// In view mode, don't render the empty Status button for blog posts without a content state.
		!(
			isViewMode &&
			contentType === 'blogpost' &&
			!(effectiveState.contentState || hasApprovalStatus) &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true)
		);

	if (shouldRenderUI && isPublishingContent) {
		// isPublishingContent is an optional prop that we only pass to this component if a page has a content state.
		// Therefore if it's true, means we're publishing a page with a content state so fire the analytic event
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'publishedWithContentState',
				actionSubject: 'page',
				objectId: contentId,
				source: 'editorContentState',
				attributes: {
					contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
				},
			},
		}).fire();
	}

	return (
		<Fragment>
			{isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
				<QuickReloadSubscriber name="content-state" types={[ReloadType.content]} reload={refetch} />
			)}
			{shouldRenderUI && (
				<LazyEditorContentStateComponent
					contentId={contentId}
					contentType={contentType}
					contentStateState={
						{ ...contentStateState, state: effectiveState } as ContentStateStateContainer
					}
					userOffline={userOffline}
					onContentStateMenuToggle={onContentStateMenuToggle}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					renderPlusButtonEmptyState={renderPlusButtonEmptyState}
					isDetailsPanel={isDetailsPanel}
					onClick={onClick}
					isViewMode={isViewMode}
					canEdit={canEdit}
				/>
			)}
		</Fragment>
	);
};

const EditorContentStateWithErrorBoundary = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(EditorContentStateDataGetter);

const contentStateContainers = [ContentStateStateContainer];

export const EditorContentState = (props: any) => (
	<Subscribe to={contentStateContainers}>
		{(contentStateState: ContentStateStateContainer) => (
			<EditorContentStateWithErrorBoundary {...props} contentStateState={contentStateState} />
		)}
	</Subscribe>
);
